import { ThreadList, useCordThreadActivitySummary } from "@cord-sdk/react";
import classNames from "classnames";
import { cordEnabled, NoCordMsg } from "frontend/hooks/use-cord";
import { SyncService } from "frontend/services/syncService";
import { useStateValue } from "frontend/state";
import { useBoardValue } from "frontend/state/board-state";
import StyledButton, { ButtonSize, ButtonStyle } from "frontend/ui-components/styled-button";
import { atom, useAtom } from "jotai";
import React from "react";
import consts from "shared/consts";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { CursorType } from "../cursor-type";
import { FramesList } from "../frames-list";
import style from "./side-panel.module.css";
import { getPathPrefix } from "../../utils/getPathPrefix";
import { isCIM } from "utils/url-utils";
import tracking from "frontend/tracking";
import { CloseIcon } from "frontend/icons/close";

export enum Tab {
  frames,
  comments,
}

function tabTitle(tab: Tab) {
  switch (tab) {
    case Tab.frames: {
      return "Frames";
    }
    case Tab.comments: {
      return "Comments";
    }
  }
}

// Atom to remember start even if side panel is closed
export const sidePaneState = atom(Tab.frames);

export default function CanvasSidePanel({
  syncService,
  onFocusElement,
  onDeleteElement,
  cordAuthToken,
  changeCommentStatus,
  onCursorTypeChange,
  onClose,
}: {
  syncService: SyncService<RW> | null;
  onFocusElement: (id: string) => void;
  onDeleteElement: (id: string) => void;
  cordAuthToken?: string | null;
  changeCommentStatus: (id: string, open: boolean) => void;
  onCursorTypeChange: (type: CursorType) => void;
  onClose: () => void;
}) {
  const [{ board }, _] = useBoardValue();
  const [{ account }, __] = useStateValue() as any;
  const [selectedTab, setSelectedTab] = useAtom(sidePaneState);

  const comments = useCordThreadActivitySummary({ documentId: board?.documentId ?? false });

  function renderTab(tab: Tab, isDisabled = false) {
    const isSelected = selectedTab == tab;
    return (
      <div
        className={classNames(style.tab, { [style.selectedTab]: isSelected, [style.disabledTag]: isDisabled })}
        onClick={() => setSelectedTab(tab)}
      >
        <span>{tabTitle(tab)}</span>
      </div>
    );
  }

  function renderFrames() {
    return (
      <>
        <MemoedFramesList
          syncService={syncService}
          onFrameSelected={onFocusElement}
          onFrameDeleted={onDeleteElement}
          onCursorTypeChange={onCursorTypeChange}
        />
      </>
    );
  }

  function renderCommentsEmptyState() {
    return (
      <div className={style.commentsEmptyState}>
        <img src={getPathPrefix("/images/no-comments.png")} />
        <div>
          <p>No comments yet! </p>
          <p>Be the first to ask a question, give feedback and mention your team member.</p>
        </div>
        <StyledButton
          title={"Add a comment"}
          style={ButtonStyle.outline}
          size={ButtonSize.small}
          onClick={() => {
            onCursorTypeChange && onCursorTypeChange(CursorType.comment);
          }}
          tabIndex={null}
        />
      </div>
    );
  }

  function renderComments() {
    if (board?.accountId != account?.id) {
      return <div style={{ padding: "30px 0 0 30px" }}>{NoCordMsg}</div>;
    }

    if (isCIM()) {
      tracking.trackAnalyticsEvent("canvas_monday_add_comment_from_side_panel", {
        boardId: board?.id,
        accountId: account?.id,
      });
      return <div style={{ padding: "30px 0 0 30px" }}>Comments are not available</div>;
    }

    return (
      <>
        {!comments?.total ? (
          renderCommentsEmptyState()
        ) : (
          <ThreadList
            location={{ documentId: board!.documentId }}
            onThreadClick={(threadId) => onFocusElement(consts.CANVAS_ELEMENTS.COMMENT + "-" + threadId)}
            onThreadResolve={(arg) => {
              changeCommentStatus(arg.threadID, false);
            }}
            onThreadReopen={(arg) => {
              changeCommentStatus(arg.threadID, true);
            }}
          />
        )}
      </>
    );
  }

  function renderContent() {
    switch (selectedTab) {
      case Tab.frames: {
        return renderFrames();
      }
      case Tab.comments: {
        return renderComments();
      }
    }
  }

  return (
    <div
      className={style.container}
      style={{
        paddingTop: document.getElementById("app-header-container")?.clientHeight || 100,
      }}
    >
      <div className={style.contentContainer}>
        <CloseIcon className={style.closeIcon} onClick={onClose} />
        <div className={style.tabs}>
          {renderTab(Tab.frames)}
          {cordEnabled() && renderTab(Tab.comments, !cordAuthToken)}
        </div>
        {renderContent()}
      </div>
    </div>
  );
}

const MemoedFramesList = React.memo(FramesList);
